import { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import SearchIcon from "@mui/icons-material/Search";

import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import useUserData from "../../hooks/useUserData";
import useToaster from "../../hooks/useToaster";
import useMediaQuery from "../../hooks/useMediaQuery";
import CommonPage from "../../components/common-page";
import VirtualizedTable from "../../components/virtualTable";
import { tableLayout } from "./tableLayout";
import { pickMobileColumns } from "../../components/virtualTable/mobileColumns";
import {
  CreateUserDialog,
  EditUserDialog,
  ResetPasswordDialog,
} from "./dialogs";

// On a phone, show Name, Role and the row actions; email/group/active are
// available by editing the user. Role needs enough width for the chip label
// ("Admin"/"User") not to clip to a single letter.
const MOBILE_COLUMN_WIDTHS = { name: 100, role: 76, actions: 96 };

export default function Users() {
  const [
    {
      pages: { users: text },
    },
  ] = useLanguage();
  const { listUsers, createUser, updateUser, setUserPassword } = useContent();
  const { addToaster } = useToaster();
  const currentUser = useUserData();
  const isMobile = useMediaQuery();
  const columns = isMobile
    ? pickMobileColumns(tableLayout, MOBILE_COLUMN_WIDTHS)
    : tableLayout;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      // safeFetch already toasts
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = users.map((u) => ({
      ...u,
      group: u.group || text.groupPlaceholder,
    }));
    if (!q) return base;
    return base.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [users, search, text.groupPlaceholder]);

  const handleCreate = async (payload) => {
    try {
      const created = await createUser(payload);
      setUsers((prev) => [...prev, created]);
      addToaster({ message: text.toaster.created, severity: "success" });
    } catch {
      // toaster already raised
      throw new Error("create failed");
    }
  };

  const handleEdit = async (payload) => {
    if (!editTarget) return;
    const id = editTarget.id;
    try {
      const updated = await updateUser(id, payload);
      setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
      addToaster({ message: text.toaster.updated, severity: "success" });
    } catch {
      throw new Error("update failed");
    }
  };

  const handleResetPassword = async (password) => {
    if (!resetTarget) return;
    try {
      await setUserPassword(resetTarget.id, password);
      addToaster({ message: text.toaster.passwordReset, severity: "success" });
    } catch {
      throw new Error("reset failed");
    }
  };

  const handleActiveChange = async (id, checked) => {
    if (id === currentUser?.id) {
      addToaster({ message: text.toaster.error, severity: "warning" });
      return;
    }
    const prev = users;
    setUsers((u) => u.map((x) => (x.id === id ? { ...x, active: checked } : x)));
    try {
      const updated = await updateUser(id, { active: checked });
      setUsers((u) => u.map((x) => (x.id === id ? updated : x)));
      addToaster({
        message: checked ? text.toaster.activated : text.toaster.deactivated,
        severity: "success",
      });
    } catch {
      setUsers(prev);
    }
  };

  const resolveRowId = (e) =>
    e?.target?.id ||
    e?.target?.parentNode?.id ||
    e?.target?.parentNode?.parentNode?.id ||
    e?.target?.parentNode?.parentNode?.parentNode?.id;

  const handleEditClick = (e) => {
    const id = resolveRowId(e);
    const user = users.find((u) => u.id === id);
    if (user) setEditTarget(user);
  };

  const handleResetClick = (e) => {
    const id = resolveRowId(e);
    const user = users.find((u) => u.id === id);
    if (user) setResetTarget(user);
  };

  return (
    <CommonPage
      title={text.title}
      subtitle={text.subtitle}
      maxWidth={false}
      action={{
        label: text.new.long,
        onClick: () => setCreateOpen(true),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          fullWidth
          size="small"
          placeholder={text.search.placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <VirtualizedTable
          columns={columns}
          data={loading ? [] : rows}
          disableBorder
          columnHandlers={{
            active: {
              onChange: handleActiveChange,
              tooltipOn: text.activeTooltip.on,
              tooltipOff: text.activeTooltip.off,
              disabled: (row) => row.id === currentUser?.id,
            },
          }}
          actions={(row) => [
            <Tooltip key="edit-t" title={text.actions.edit} arrow placement="top">
              <IconButton key="edit" onClick={handleEditClick} color="primary">
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>,
            <Tooltip
              key="reset-t"
              title={text.actions.resetPassword}
              arrow
              placement="top"
            >
              <IconButton
                key="reset"
                onClick={handleResetClick}
                color="warning"
              >
                <LockResetRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>,
          ]}
        />
      </Box>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        text={text}
      />
      <EditUserDialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={handleEdit}
        text={text}
        user={editTarget}
        isSelf={editTarget?.id === currentUser?.id}
      />
      <ResetPasswordDialog
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onSubmit={handleResetPassword}
        text={text}
        user={resetTarget}
      />
    </CommonPage>
  );
}
